/**
 * TripletexImportModal — bulk import projects from Tripletex.
 *
 * Fetches the Tripletex project list (via backend), shows an editable
 * preview table with a checkbox column, an editable email column, and a
 * "search / filter" input to narrow the list. Admin selects which rows
 * to import and hits "Importere X prosjekter".
 *
 * Rows already present in doserne_projects (matched by tripletex_id)
 * come back with `already_imported: true` and are shown but unchecked.
 */
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, X, Search, RefreshCw, Download, CheckSquare, Square } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TripletexImportModal({ onClose, onImported, authHeader }) {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [rows, setRows] = useState([]); // {tripletex_id, name, number, customer_name, customer_email, address, already_imported, _selected, _email_override}
  const [q, setQ] = useState("");
  const [hideImported, setHideImported] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/tripletex/projects`, authHeader());
      const enhanced = (data.projects || []).map((r) => ({
        ...r,
        _selected: !r.already_imported,
        _email_override: r.customer_email || "",
      }));
      setRows(enhanced);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke hente Tripletex-prosjekter");
      setRows([]);
    }
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return rows.filter((r) => {
      if (hideImported && r.already_imported) return false;
      if (!term) return true;
      const hay = [r.name, r.number, r.customer_name, r.address, r._email_override].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(term);
    });
  }, [rows, q, hideImported]);

  const selectedCount = filtered.filter((r) => r._selected).length;
  const allVisibleSelected = filtered.length > 0 && selectedCount === filtered.length;

  const toggleAllVisible = () => {
    const next = !allVisibleSelected;
    const visibleIds = new Set(filtered.map((r) => r.tripletex_id));
    setRows((cur) => cur.map((r) => visibleIds.has(r.tripletex_id) ? { ...r, _selected: next } : r));
  };

  const setField = (tripletex_id, key, val) => {
    setRows((cur) => cur.map((r) => r.tripletex_id === tripletex_id ? { ...r, [key]: val } : r));
  };

  const doImport = async () => {
    const chosen = rows.filter((r) => r._selected && !r.already_imported);
    if (chosen.length === 0) {
      toast.warning("Ingen prosjekter valgt");
      return;
    }
    if (!window.confirm(`Importere ${chosen.length} prosjekter fra Tripletex?`)) return;
    setBusy(true);
    try {
      const payload = {
        projects: chosen.map((r) => ({
          tripletex_id: r.tripletex_id,
          name: r.name || "",
          number: r.number ? String(r.number) : "",
          customer_name: r.customer_name || "",
          customer_email: r._email_override || "",
          customer_phone: r.customer_phone || "",
          address: r.address || "",
        })),
      };
      const { data } = await axios.post(`${API}/tripletex/import`, payload, authHeader());
      toast.success(`${data.created} opprettet · ${data.skipped} hoppet over`);
      onImported?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import feilet");
    }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose} data-testid="tripletex-modal">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-5xl h-[95vh] sm:h-[85vh] flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 flex-shrink-0">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-black">Tripletex</div>
            <h2 className="text-xl font-black text-slate-900">Importere prosjekter</h2>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={load} disabled={loading} className="w-9 h-9 rounded-lg bg-slate-100 hover:bg-slate-200 flex items-center justify-center" title="Last på nytt" data-testid="tripletex-refresh">
              <RefreshCw className={`w-4 h-4 text-slate-600 ${loading ? "animate-spin" : ""}`} />
            </button>
            <button onClick={onClose} className="w-9 h-9 rounded-lg hover:bg-slate-100 flex items-center justify-center" data-testid="tripletex-close">
              <X className="w-5 h-5 text-slate-500" />
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="px-5 py-3 border-b border-slate-100 flex flex-wrap items-center gap-2 flex-shrink-0">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Søk nummer, navn, kunde, adresse…" className="w-full pl-9 pr-3 py-2 rounded-lg border border-slate-300 text-sm focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20" data-testid="tripletex-search" />
          </div>
          <label className="inline-flex items-center gap-2 text-xs text-slate-600 select-none">
            <input type="checkbox" checked={hideImported} onChange={(e) => setHideImported(e.target.checked)} data-testid="tripletex-hide-imported" />
            Skjul allerede importerte
          </label>
          <button
            onClick={toggleAllVisible}
            disabled={filtered.length === 0}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-300 hover:bg-slate-50 text-xs font-semibold text-slate-700"
            data-testid="tripletex-toggle-all"
          >
            {allVisibleSelected ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
            {allVisibleSelected ? "Fjern alle" : "Merk alle"}
          </button>
        </div>

        {/* Table */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-slate-500">
              <Loader2 className="w-6 h-6 animate-spin mr-2" /> Henter fra Tripletex…
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16 text-sm text-slate-500 italic">Ingen prosjekter matcher.</div>
          ) : (
            <table className="w-full text-sm" data-testid="tripletex-table">
              <thead className="sticky top-0 bg-slate-50 border-b border-slate-200 z-10">
                <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 font-bold">
                  <th className="px-3 py-2 w-10"></th>
                  <th className="px-2 py-2 w-16">Nr</th>
                  <th className="px-2 py-2">Adresse / Prosjektnavn</th>
                  <th className="px-2 py-2">Kunde</th>
                  <th className="px-2 py-2 w-52">E-post (rediger)</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr
                    key={r.tripletex_id}
                    className={`border-b border-slate-100 hover:bg-slate-50 ${r.already_imported ? "opacity-50" : ""}`}
                    data-testid={`tripletex-row-${r.tripletex_id}`}
                  >
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        checked={r._selected}
                        disabled={r.already_imported}
                        onChange={(e) => setField(r.tripletex_id, "_selected", e.target.checked)}
                        data-testid={`tripletex-check-${r.tripletex_id}`}
                      />
                    </td>
                    <td className="px-2 py-2 font-mono text-xs text-slate-600">{r.number}</td>
                    <td className="px-2 py-2 font-semibold text-slate-900 truncate max-w-[300px]">
                      {r.name}
                      {r.already_imported && <span className="ml-1.5 text-[9px] uppercase tracking-widest text-slate-400 font-black">· Importert</span>}
                    </td>
                    <td className="px-2 py-2 text-slate-700 truncate max-w-[220px]">{r.customer_name}</td>
                    <td className="px-2 py-2">
                      <input
                        type="email"
                        value={r._email_override}
                        onChange={(e) => setField(r.tripletex_id, "_email_override", e.target.value)}
                        placeholder="kunde@..."
                        className="w-full px-2 py-1 border border-slate-200 rounded text-xs focus:outline-none focus:border-emerald-500"
                        data-testid={`tripletex-email-${r.tripletex_id}`}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-between gap-3 flex-shrink-0 bg-slate-50">
          <div className="text-xs text-slate-600">
            <span className="font-black text-slate-900">{selectedCount}</span> valgt · <span className="font-semibold">{filtered.length}</span> vist · <span>{rows.length} totalt</span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-300 text-sm font-semibold text-slate-700 hover:bg-slate-100" data-testid="tripletex-cancel">
              Avbryt
            </button>
            <button
              onClick={doImport}
              disabled={busy || selectedCount === 0}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-sm font-black"
              style={{ color: "#fff" }}
              data-testid="tripletex-import-btn"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              Importere {selectedCount} prosjekter
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
